import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { BatchAction, MessagePage, MessageSummary, SharedPreferences } from '@dts/core';
import { api } from './api';
import type { View } from './store';

export const qk = {
  me: ['me'] as const,
  folders: ['folders'] as const,
  messages: (v: Extract<View, { kind: 'mail' }>) => ['messages', v.folder ?? '*', v.recipient ?? '', v.domain ?? '', v.search ?? '', v.filter ?? 'all'] as const,
  message: (id: string) => ['message', id] as const,
  thread: (id: string) => ['thread', id] as const,
  summary: (id: string) => ['summary', id] as const,
  prefs: ['prefs'] as const,
  contacts: ['contacts'] as const,
  brief: ['brief'] as const,
  aiStatus: ['ai-status'] as const,
};

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: api.me, staleTime: 5 * 60_000 });
}

export function useMyAddresses(): string[] {
  const me = useMe().data;
  return me ? [me.user.email, ...me.addresses.map((a) => a.email)].map((a) => a.toLowerCase()) : [];
}

export function useFolders() {
  return useQuery({ queryKey: qk.folders, queryFn: api.folders, refetchInterval: 5 * 60_000 });
}

export function useAiStatus() {
  return useQuery({ queryKey: qk.aiStatus, queryFn: api.aiStatus, staleTime: 10 * 60_000 });
}

/** The UnRead view: unread mail in every folder except Junk and Trash. */
export const UNREAD_EXCLUDED = ['Junk', 'Trash'];
export const isUnreadEverywhere = (v: Extract<View, { kind: 'mail' }>) => !v.folder && !v.search && !v.recipient && !v.domain && v.filter === 'unread';

export function useMessages(v: Extract<View, { kind: 'mail' }>) {
  return useInfiniteQuery({
    queryKey: qk.messages(v),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.listMessages({
        folder: v.search ? undefined : v.folder,
        recipient: v.recipient,
        domain: v.domain,
        search: v.search,
        unread: v.filter === 'unread',
        flagged: v.filter === 'flagged',
        exclude: isUnreadEverywhere(v) ? UNREAD_EXCLUDED.join(',') : undefined,
        top: 60,
        continuation: pageParam,
      }),
    getNextPageParam: (last) => last.continuation ?? undefined,
    // Server push (Storage Queue events) drives refreshes; this is only a safety net.
    refetchInterval: 5 * 60_000,
  });
}

export function useMessage(id: string | null) {
  return useQuery({ queryKey: qk.message(id ?? ''), queryFn: () => api.getMessage(id!, false), enabled: !!id, staleTime: 5 * 60_000 });
}

export function useThread(id: string | null) {
  return useQuery({ queryKey: qk.thread(id ?? ''), queryFn: () => api.thread(id!), enabled: !!id, staleTime: 30_000 });
}

export function useContacts() {
  return useQuery({ queryKey: qk.contacts, queryFn: () => api.contacts(), staleTime: 60_000 });
}

export function usePrefs() {
  return useQuery({ queryKey: qk.prefs, queryFn: api.preferences, staleTime: 5 * 60_000 });
}

export function useSavePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SharedPreferences) => {
      const current = qc.getQueryData<SharedPreferences>(qk.prefs) ?? (await api.preferences());
      const next: SharedPreferences = {
        ...current,
        ...patch,
        ai: { ...current.ai, ...patch.ai },
        crm: { ...current.crm, ...patch.crm },
        signatures: patch.signatures ?? current.signatures,
      };
      qc.setQueryData(qk.prefs, next);
      return api.savePreferences(next);
    },
    onSuccess: (data) => qc.setQueryData(qk.prefs, data),
  });
}

/** Optimistically patches cached message summaries everywhere they appear. */
export function patchCachedMessages(qc: ReturnType<typeof useQueryClient>, ids: string[], patch: (m: MessageSummary) => MessageSummary | null) {
  const set = new Set(ids);
  qc.setQueriesData<InfiniteData<MessagePage>>({ queryKey: ['messages'] }, (data) =>
    data
      ? {
          ...data,
          pages: data.pages.map((p) => ({
            ...p,
            items: p.items.flatMap((m) => {
              if (!set.has(m.id)) return [m];
              const next = patch(m);
              return next ? [next] : [];
            }),
          })),
        }
      : data,
  );
}

/**
 * Filing mail as junk blocks the sender's domain and sweeps the rest of their mail into Junk; say so, with a way
 * back (Undo lifts the block and returns the swept mail to the Inbox).
 */
export function announceBlocked(entries: string[], alsoJunked: string[] = [], onUndone?: () => void) {
  const names = entries.length === 1 ? entries[0] : `${entries.length} senders`;
  const n = alsoJunked.length;
  toast.success(`Blocked ${names}`, {
    description: n > 0 ? `Moved ${n} other email${n === 1 ? '' : 's'} from them to Junk. Future mail goes there too.` : 'Future mail from them goes straight to Junk.',
    action: {
      label: 'Undo',
      onClick: () =>
        void (async () => {
          await Promise.all(entries.map((e) => api.removeFromSenderList('block', e)));
          for (let i = 0; i < alsoJunked.length; i += 200) await api.batch(alsoJunked.slice(i, i + 200), 'notjunk');
        })()
          .then(() => (toast.success(`Unblocked ${names}`), onUndone?.()))
          .catch(() => toast.error('Could not undo the block')),
    },
  });
}

/** Message actions with optimistic list updates. */
export function useMailActions() {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['messages'] });
    qc.invalidateQueries({ queryKey: qk.folders });
  };
  const run = async (ids: string[], action: BatchAction, folder?: string) => {
    const removes = ['move', 'archive', 'trash', 'delete', 'junk', 'notjunk'].includes(action);
    patchCachedMessages(qc, ids, (m) => {
      if (removes) return null;
      if (action === 'read') return { ...m, isRead: true };
      if (action === 'unread') return { ...m, isRead: false };
      if (action === 'flag') return { ...m, isFlagged: true };
      if (action === 'unflag') return { ...m, isFlagged: false };
      return m;
    });
    try {
      if (ids.length === 1 && (action === 'read' || action === 'unread' || action === 'flag' || action === 'unflag')) {
        await api.updateMessage(ids[0], action === 'read' || action === 'unread' ? { isRead: action === 'read' } : { isFlagged: action === 'flag' });
      } else {
        const r = await api.batch(ids, action, folder);
        if (r.blocked?.length) announceBlocked(r.blocked, r.alsoJunked, refresh);
        if (r.unsubscribed?.length)
          toast.success(`Unsubscribed from ${r.unsubscribed.length === 1 ? r.unsubscribed[0] : `${r.unsubscribed.length} mailing lists`}`, {
            description: r.unsubscribed.length > 1 ? r.unsubscribed.join(', ') : 'Their one-click unsubscribe confirmed it.',
          });
      }
    } finally {
      refresh();
      ids.forEach((id) => qc.invalidateQueries({ queryKey: qk.message(id) }));
    }
  };
  /** Marks every unread email outside Junk and Trash as read, not just the ones loaded; resolves to how many. */
  const markAllRead = async (onProgress?: (done: number, total: number) => void) => {
    const ids: string[] = [];
    let continuation: string | undefined;
    do {
      const page = await api.listMessages({ unread: true, exclude: UNREAD_EXCLUDED.join(','), top: 200, continuation });
      ids.push(...page.items.filter((m) => !m.isRead && !UNREAD_EXCLUDED.includes(m.folder)).map((m) => m.id));
      continuation = page.continuation ?? undefined;
    } while (continuation);
    patchCachedMessages(qc, ids, (m) => ({ ...m, isRead: true }));
    try {
      for (let i = 0; i < ids.length; i += 200) {
        await api.batch(ids.slice(i, i + 200), 'read');
        onProgress?.(Math.min(i + 200, ids.length), ids.length);
      }
    } finally {
      refresh();
    }
    return ids.length;
  };
  return { run, refresh, markAllRead };
}
